import { ArrowUpTrayIcon, ArrowDownTrayIcon } from '@heroicons/react/24/solid';
import { useReactFlow } from '@xyflow/react';
import { useRef, useState, useEffect, useCallback } from 'react';
import { CameraIcon } from 'src/components/icons/CameraIcon';
import { ConfigIcon } from 'src/components/icons/ConfigIcon';
import { DeviceIpIcon } from 'src/components/icons/DeviceIpIcon';
import { DownloadFile } from 'src/components/icons/DownloadFile';
import { FrameIcon } from 'src/components/icons/FrameIcon';
import { GlobalSettingIcon } from 'src/components/icons/GlobalSettingIcon';
import { LockIcon } from 'src/components/icons/LockIcon';
import { LockOpenIcon } from 'src/components/icons/LockOpenIcon';
import { ProjectDataIcon } from 'src/components/icons/ProjectDataIcon';
import { SettingIcon } from 'src/components/icons/SettingIcon';
import { SubnetIcon } from 'src/components/icons/SubnetIcon';
import { SwitchIcon } from 'src/components/icons/SwitchIcon';
import { nodeZIndex } from 'src/config';
import { useGlobalContext } from 'src/context/global/use-global-context';
import { useImportConfiguration } from 'src/context/import-configuration';
import { twMerge } from 'tailwind-merge';

import DialogExportDocumentation from '../dialog/export-documentation';
import { EDataType } from '../models/node-data';
import { AppNode } from '../models/node-model';
import './hexagon.scss';
import MenuDevices from './MenuDevice';
import MenuSettings from './MenuSettings';
import MenuSwitches from './MenuSwitches';
import { IMenu } from './type';

type BlobResult = { blob: Blob; filename: string };

interface IProps {
  disabledExport?: boolean;
  onExport?: () => void;
  disabledImport?: boolean;
  onImport?: () => void;
  isInteractive?: boolean;
  setIsInteractive?: (value: boolean) => void;
  onExportFrame?: (type: 'pdf') => void;
  onExportScreenshot?: () => void;
  onGetFrameBlob?: () => Promise<BlobResult | null>;
  onGetScreenshotBlob?: () => Promise<BlobResult | null>;
}

function FlowMenu({
  disabledExport,
  onExport,
  disabledImport,
  onImport,
  isInteractive,
  setIsInteractive,
  onExportFrame,
  onExportScreenshot,
  onGetFrameBlob,
  onGetScreenshotBlob,
}: IProps) {
  const [active, setActive] = useState<string | null>(null);
  const {
    exportSettings,
    setExportSettings,
    showDialogs,
    setShowDialogs,
    setDisableFlow,
  } = useGlobalContext();
  const { onImportConfiguration } = useImportConfiguration();
  const { getNodes, setNodes } = useReactFlow();
  const nodes = getNodes();

  const menuContainerRef = useRef<HTMLDivElement>(null);

  // Manage ProjectFrame node visibility
  const handleToggleProjectFrame = useCallback(
    (isVisible: boolean) => {
      setExportSettings({ isVisible });

      const existingFrameNode = nodes.find(
        (node) => node.type === EDataType.ProjectFrame,
      );
      if (isVisible) {
        if (existingFrameNode) {
          setNodes((nds) =>
            nds.map((node) =>
              node.id === existingFrameNode.id
                ? {
                    ...node,
                    width: exportSettings?.width || 794,
                    height: exportSettings?.height || 1123,
                    position: {
                      x: exportSettings?.positionX || 0,
                      y: exportSettings?.positionY || 0,
                    },
                    zIndex: nodeZIndex(EDataType.ProjectFrame),
                    frameOrientation:
                      exportSettings?.frameOrientation || 'portrait',
                  }
                : node,
            ),
          );
        } else {
          // Add new ProjectFrame node
          const frameNode: AppNode = {
            id: 'project-frame-node',
            type: EDataType.ProjectFrame,
            position: {
              x: exportSettings?.positionX || 0,
              y: exportSettings?.positionY || 0,
            },
            width: exportSettings?.width || 794,
            height: exportSettings?.height || 1123,
            data: {
              key: EDataType.ProjectFrame,
              label: 'Project Frame',
              frameOrientation: exportSettings?.frameOrientation || 'portrait',
              category: {
                name: 'Project Frame',
                color: '#ffffff',
                type: EDataType.ProjectFrame,
              },
            } as any,
            zIndex: nodeZIndex(EDataType.ProjectFrame),
            selectable: true,
            draggable: true,
            deletable: false,
          };
          setNodes((nds) => [frameNode, ...nds]);
        }
      } else if (!isVisible && existingFrameNode) {
        // Save frame state before removing
        setNodes((nds) =>
          nds.filter((node) => node.id !== existingFrameNode.id),
        );
      }
    },
    [exportSettings, nodes, setNodes, setExportSettings],
  );

  const settingActions: IMenu[] = [
    {
      key: 'project-data',
      title: 'Project Data',
      icon: <ProjectDataIcon className="h-5 w-5" />,
      action: () => {
        if (showDialogs.projectData) return;
        setActive(null);
        setShowDialogs({ projectData: true });
      },
    },
    {
      key: 'project-frame',
      title: exportSettings?.isVisible ? 'Hide Frame' : 'Show Frame',
      icon: <FrameIcon className="h-5 w-5" />,
      action: () => {
        setActive(null);
        handleToggleProjectFrame(!exportSettings?.isVisible);
      },
    },
    {
      key: 'lock',
      title: !isInteractive ? 'Unlock' : 'Lock',
      icon: !isInteractive ? (
        <LockOpenIcon className="h-5 w-5" />
      ) : (
        <LockIcon className="h-5 w-5" />
      ),
      action: () => {
        setIsInteractive?.(!isInteractive);
      },
    },
    {
      key: 'export',
      title: 'Export Project',
      disabled: disabledExport,
      icon: <ArrowDownTrayIcon className="h-5 w-5" />,
      action: onExport,
    },
    {
      key: 'import',
      title: 'Import Project',
      disabled: disabledImport,
      icon: <ArrowUpTrayIcon className="h-5 w-5" />,
      action: onImport,
    },
    {
      key: 'import-configuration',
      title: 'Import Configuration',
      disabled: disabledImport,
      icon: <ArrowUpTrayIcon className="h-5 w-5" />,
      action: () => {
        onImportConfiguration();
      },
    },
    {
      key: 'export-documentation',
      title: 'Export Documentation',
      disabled: disabledExport,
      icon: <DownloadFile className="h-5 w-5" />,
      action: () => {
        if (showDialogs.exportDocumentation) return;
        setActive(null);
        setShowDialogs({ exportDocumentation: true });
      },
    },
  ];

  const menus: IMenu[] = [
    {
      key: 'switch',
      title: 'Switches',
      icon: <SwitchIcon className="h-10 w-10" />,
      component: <MenuSwitches />,
    },
    {
      key: 'device',
      title: 'Peripheral Devices',
      icon: <CameraIcon className="h-9 w-9" />,
      component: <MenuDevices />,
    },
    {
      key: 'vlan',
      title: 'VLANs and subnets',
      icon: <SubnetIcon className="h-11 w-11" />,
      action: () => {
        if (showDialogs.vlan) return;
        setActive(null);
        setShowDialogs({ vlan: true });
      },
    },
    {
      key: 'device-ip',
      title: 'Device IP addresses',
      icon: <DeviceIpIcon className="h-8 w-8" />,
      action: () => {
        if (showDialogs.setting) return;
        setActive(null);
        setShowDialogs({ setting: true });
      },
    },
    {
      key: 'global-settings',
      title: 'Global Settings',
      icon: <GlobalSettingIcon className="h-9 w-9" />,
      action: () => {
        if (showDialogs.globalSettings) return;
        setActive(null);
        setShowDialogs({ globalSettings: true });
      },
    },
    {
      key: 'config',
      title: 'Export configuration files',
      icon: <ConfigIcon className="h-9 w-9" />,
      action: () => {
        if (showDialogs.package) return;
        setActive(null);
        setShowDialogs({ package: true });
      },
    },
    {
      key: 'settings',
      title: 'Project',
      icon: <SettingIcon className="h-9 w-9" />,
      component: <MenuSettings actions={settingActions} />,
    },
  ];

  const renderComponent = (key: string) => {
    const menu = menus.find((m) => m.key === key);
    if (!menu) return null;
    return menu.component;
  };

  useEffect(() => {
    if (setDisableFlow) {
      setDisableFlow(active !== null);
    }
  }, [active, setDisableFlow]);

  // check click outside menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuContainerRef.current &&
        !menuContainerRef.current.contains(event.target as Node)
      ) {
        setActive(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  return (
    <div
      className="flex flex-col items-center justify-center"
      id="flow-menu"
      ref={menuContainerRef}
    >
      {active && (
        <div
          className={twMerge([
            'flow-menu absolute inset-x-0 bottom-24 z-50 overflow-x-auto',
          ])}
        >
          <div
            data-testid="menu-component"
            className="z-50 flex min-h-20 w-[100vw] min-w-96 flex-col-reverse gap-2"
          >
            {renderComponent(active)}
          </div>
        </div>
      )}
      <div className="flow-menu absolute inset-x-0 bottom-4 z-20 flex flex-col items-center justify-center">
        <div className="relative flex gap-4">
          {menus.map((menu) => (
            <div
              data-testid={`menu-${menu.key}`}
              className="relative h-14 w-14"
              key={menu.key}
            >
              <div
                className={twMerge([
                  'hexagon',
                  active === menu.key ? 'active' : 'inactive',
                  menu.disabled ? 'disabled cursor-not-allowed' : '',
                ])}
              >
                <div
                  className={twMerge([
                    'hexagon inner',
                    active === menu.key ? 'active' : 'inactive',
                    menu.disabled ? 'disabled cursor-not-allowed' : '',
                  ])}
                >
                  <div
                    className={twMerge([
                      'hexagon content',
                      active === menu.key ? 'active' : 'inactive',
                      menu.disabled ? 'disabled cursor-not-allowed' : '',
                    ])}
                  >
                    <div className="absolute inset-0 z-10 flex items-center justify-center">
                      <button
                        key={menu.key}
                        type="button"
                        data-testid={`menu-${menu.key}-button`}
                        data-tooltip-id="app-tooltip"
                        data-tooltip-content={menu.title}
                        onClick={(
                          event: React.MouseEvent<HTMLButtonElement>,
                        ) => {
                          if (menu.action) {
                            if (menu.disabled) return;
                            menu.action(event);
                            return;
                          }
                          setActive(active === menu.key ? null : menu.key);
                        }}
                        className={twMerge([
                          'cursor-pointer',
                          'border-none outline-none ring-0 focus:outline-none focus:ring-0',
                          'bg-transparent p-2 text-blue-400',
                          // active === menu.key ? 'text-white' : 'text-blue-500',
                          menu.disabled
                            ? 'cursor-not-allowed text-gray-400'
                            : '',
                        ])}
                      >
                        {menu.icon}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {showDialogs.exportDocumentation &&
        onExportFrame &&
        onExportScreenshot &&
        onGetFrameBlob &&
        onGetScreenshotBlob && (
          <DialogExportDocumentation
            onExportFrame={onExportFrame}
            onExportScreenshot={onExportScreenshot}
            onGetFrameBlob={onGetFrameBlob}
            onGetScreenshotBlob={onGetScreenshotBlob}
          />
        )}
    </div>
  );
}

export default FlowMenu;
